"use client";

import { useEffect } from "react";
import { PageKey, MetadataOptions } from "@/lib/metadata";

interface MetadataProps {
  pageKey: PageKey;
  options?: MetadataOptions;
  params?: Record<string, string>;
}

/**
 * Client component for updating page metadata dynamically
 * Use this in client components where you can't use static metadata exports
 */
export default function Metadata({
  pageKey,
  options = {},
  params = {},
}: MetadataProps) {
  useEffect(() => {
    // Import the metadata configuration dynamically to avoid SSR issues
    import("@/lib/metadata").then(({ PAGE_METADATA }) => {
      const pageConfig = PAGE_METADATA[pageKey];

      // Use custom values or fall back to page config
      let title = options.title || pageConfig.title;
      let description = options.description || pageConfig.description;
      const keywords = options.keywords || pageConfig.keywords;

      // Handle dynamic content based on page type and params
      switch (pageKey) {
        case "checkout":
          if (params.ticketId) {
            title = `Checkout | Complete Your Custom Dress Order #${params.ticketId.slice(0, 8)}`;
            description = `Complete your custom dress order #${params.ticketId.slice(0, 8)} with secure payment processing. Review your design and finalize your made-to-measure dress purchase.`;
          }
          break;

        case "ticket":
          if (params.id) {
            title = `Order #${params.id.slice(0, 8)} | Track Your Custom Dress`;
            description = `Track your custom dress order #${params.id.slice(0, 8)}. View design details, measurements, progress updates, and communicate with manufacturers.`;
          }
          break;

        case "success":
          if (params.ticketId) {
            title = `Order #${params.ticketId.slice(0, 8)} Confirmed | Your Custom Dress is Being Made`;
            description = `Your custom dress order #${params.ticketId.slice(0, 8)} has been confirmed! Track progress and communicate with manufacturers. Your dress will be ready in 2-3 months.`;
          }
          break;

        case "manufacturers":
          if (params.companyId && params.companyName) {
            title = `${params.companyName} - Manufacturer Profile | Build-a-Dress`;
            description = `View ${params.companyName}'s manufacturer profile, ratings, and reviews on Build-a-Dress. Learn about their experience creating custom dresses and read customer feedback.`;
          }
          break;
      }

      // Client updates bypass the root layout's title template. Add the brand once.
      title = title.includes("Build-a-Dress")
        ? title
        : `${title} | Build-a-Dress`;

      // Update document title
      if (title) {
        document.title = title;
      }

      // Update meta description
      if (description) {
        const metaDescription = document.querySelector(
          'meta[name="description"]'
        );
        if (metaDescription) {
          metaDescription.setAttribute("content", description);
        } else {
          const meta = document.createElement("meta");
          meta.name = "description";
          meta.content = description;
          document.head.appendChild(meta);
        }
      }

      // Update meta keywords
      if (keywords && keywords.length > 0) {
        const metaKeywords = document.querySelector('meta[name="keywords"]');
        if (metaKeywords) {
          metaKeywords.setAttribute("content", keywords.join(", "));
        } else {
          const meta = document.createElement("meta");
          meta.name = "keywords";
          meta.content = keywords.join(", ");
          document.head.appendChild(meta);
        }
      }

      // Update OpenGraph tags
      if (title) {
        const ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle) {
          ogTitle.setAttribute("content", title);
        } else {
          const meta = document.createElement("meta");
          meta.setAttribute("property", "og:title");
          meta.content = title;
          document.head.appendChild(meta);
        }
      }

      if (description) {
        const ogDescription = document.querySelector(
          'meta[property="og:description"]'
        );
        if (ogDescription) {
          ogDescription.setAttribute("content", description);
        } else {
          const meta = document.createElement("meta");
          meta.setAttribute("property", "og:description");
          meta.content = description;
          document.head.appendChild(meta);
        }
      }

      // Update Twitter tags
      if (title) {
        const twitterTitle = document.querySelector(
          'meta[name="twitter:title"]'
        );
        if (twitterTitle) {
          twitterTitle.setAttribute("content", title);
        } else {
          const meta = document.createElement("meta");
          meta.name = "twitter:title";
          meta.content = title;
          document.head.appendChild(meta);
        }
      }

      if (description) {
        const twitterDescription = document.querySelector(
          'meta[name="twitter:description"]'
        );
        if (twitterDescription) {
          twitterDescription.setAttribute("content", description);
        } else {
          const meta = document.createElement("meta");
          meta.name = "twitter:description";
          meta.content = description;
          document.head.appendChild(meta);
        }
      }

      // Update robots if specified
      if (options.noindex !== undefined) {
        const robots = document.querySelector('meta[name="robots"]');
        const robotsContent = options.noindex
          ? "noindex,nofollow"
          : "index,follow";

        if (robots) {
          robots.setAttribute("content", robotsContent);
        } else {
          const meta = document.createElement("meta");
          meta.name = "robots";
          meta.content = robotsContent;
          document.head.appendChild(meta);
        }
      }

      // Update canonical link
      const baseUrl =
        process.env.NEXT_PUBLIC_BASE_URL || "https://build-a-dress.com";
      const pathMap: Record<string, string> = {
        home: "/",
        create: "/create",
        gallery: "/gallery",
        faq: "/faq",
        manufacturers: "/manufacturers",
        artisans: "/artisans",
        manufacturersJoin: "/artisans/join",
        sellerOnboarding: "/seller-onboarding",
        login: "/login",
        account: "/account",
        credits: "/credits",
        requests: "/requests",
        messages: "/messages",
        success: "/success",
        thankYou: "/thank-you",
        resetPassword: "/reset-password",
        unsubscribe: "/unsubscribe",
        adminDashboard: "/admin/dashboard",
        checkout: "/checkout",
        ticket: "/ticket",
        wedding: "/custom-wedding-dresses",
        prom: "/custom-prom-dresses",
        bridesmaid: "/custom-bridesmaid-dresses",
        quinceanera: "/custom-quinceanera-dresses",
        formal: "/custom-formal-dresses",
        hijabi: "/custom-hijabi-wedding-dresses",
        modest: "/custom-modest-dresses",
      };

      const canonicalUrl =
        options.canonical || `${baseUrl}${pathMap[pageKey] || "/"}`;

      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) {
        canonicalLink.setAttribute("href", canonicalUrl);
      } else {
        canonicalLink = document.createElement("link");
        canonicalLink.setAttribute("rel", "canonical");
        canonicalLink.setAttribute("href", canonicalUrl);
        document.head.appendChild(canonicalLink);
      }
    });
  }, [pageKey, options, params]);

  // This component doesn't render anything
  return null;
}
